/**
 * SessionAudioPanel — "Paramétrer votre séance" overlay used inside Affirmations
 * and Stretching panels. Exposes:
 *  - Voice slots (admin only): Homme / Femme / (Séance complète) → upload, lock, delete
 *  - Ambient sounds: list from /api/ambient-sounds, toggle via shared ZenContext
 *  - User favorite musics: up to 5 per section, public upload/delete (cumulative playback)
 *
 * Cumulative playback: each item has its own <audio> ref, looped, with a volume slider.
 * Voice/music start when toggled and stop on overlay close — ambient uses the global
 * ZenContext mixer so it keeps playing under the panel.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Settings, Upload, Trash2, Lock, Unlock, RefreshCw, Mic, Music as MusicIcon,
  Volume2, VolumeX, Plus, Play, Pause, Headphones, Sparkles, Clock, Heart
} from 'lucide-react';
import { useZen } from '../contexts/ZenContext';
import LiveRecorder from './LiveRecorder';

const API = process.env.REACT_APP_BACKEND_URL;

const INTERVAL_OPTIONS = [
  { sec: 0, label: 'Aucune', hint: 'sans pause' },
  { sec: 10, label: '10 s', hint: 'rythme rapide' },
  { sec: 15, label: '15 s', hint: 'recommandé' },
  { sec: 30, label: '30 s', hint: 'méditatif' },
];

const VOICE_MODES = [
  { id: 'sans-voix', label: '🤫 Sans voix', sub: 'Texte uniquement' },
  { id: 'ludovic', label: '🎙 Voix de Ludovic', sub: 'Voix masculine pré-enregistrée' },
  { id: 'mon-choix', label: '🌟 Voix de mon choix', sub: 'Vos propres enregistrements' },
];

/**
 * AffirmationsSettingsBlock — voix de lecture + rythme + enregistrements personnels.
 * Visible uniquement quand SessionAudioPanel reçoit section='affirmations'.
 */
function AffirmationsSettingsBlock({ settings, onChange, theme, clipsApi, isAdmin }) {
  const [recordIndex, setRecordIndex] = useState(0); // which affirmation to record
  const voiceMode = settings?.voiceMode || 'sans-voix';
  const intervalSec = settings?.intervalSec ?? 10;

  const handleVoice = (id) => onChange?.({ voiceMode: id });
  const handleInterval = (sec) => onChange?.({ intervalSec: sec });

  // Android Chrome anti-artifact: force GPU compositing on every card
  const gpuStyle = { transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate' };

  return (
    <>
      {/* 1) Voix de lecture */}
      <section data-testid="aff-settings-voice" style={gpuStyle}>
        <div className="flex items-center gap-2 mb-2">
          <Mic className="w-4 h-4 text-amber-400" />
          <h3 className="text-amber-200 font-medium text-sm">Voix de lecture</h3>
        </div>
        <p className="text-white/55 text-[11px] leading-relaxed mb-3">
          La voix sélectionnée sera jouée automatiquement pour chaque affirmation.
        </p>
        <div className="grid grid-cols-1 gap-2">
          {VOICE_MODES.map((v) => {
            const active = voiceMode === v.id;
            return (
              <button key={v.id} onClick={() => handleVoice(v.id)}
                style={gpuStyle}
                className={`text-left px-3 py-2.5 rounded-xl border transition flex items-center justify-between ${active ? 'bg-amber-500/25 border-amber-400/60' : 'bg-slate-800 border-white/10 hover:bg-slate-700'}`}
                data-testid={`aff-voice-mode-${v.id}`}>
                <div>
                  <p className={`text-sm font-medium ${active ? 'text-amber-200' : 'text-white/90'}`}>{v.label}</p>
                  <p className="text-white/50 text-[10px]">{v.sub}</p>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${active ? 'border-amber-400 bg-amber-400' : 'border-white/30'}`} />
              </button>
            );
          })}
        </div>
      </section>

      {/* 2) Rythme (interval between affirmations) */}
      <section data-testid="aff-settings-interval" style={gpuStyle}>
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-emerald-400" />
          <h3 className="text-emerald-200 font-medium text-sm">Rythme entre les affirmations</h3>
        </div>
        <p className="text-white/60 text-[11px] leading-relaxed mb-3">
          <Heart className="w-3 h-3 inline-block text-pink-300 -mt-0.5 mr-1" />
          Le silence entre chaque affirmation vous permet de la répéter <b>mentalement ou à voix haute</b>.
          Cette répétition consciente active la mémorisation profonde, calme le système nerveux et ancre
          durablement la croyance positive dans votre subconscient. Idéal&nbsp;: <b>15 secondes</b>.
        </p>
        <div className="grid grid-cols-4 gap-2">
          {INTERVAL_OPTIONS.map((opt) => {
            const active = intervalSec === opt.sec;
            return (
              <button key={opt.sec} onClick={() => handleInterval(opt.sec)}
                style={gpuStyle}
                className={`px-2 py-2 rounded-xl border text-center transition ${active ? 'bg-emerald-500/25 border-emerald-400/60' : 'bg-slate-800 border-white/10 hover:bg-slate-700'}`}
                data-testid={`aff-interval-${opt.sec}`}>
                <p className={`text-sm font-semibold ${active ? 'text-emerald-200' : 'text-white/90'}`}>{opt.label}</p>
                <p className="text-white/50 text-[9px] mt-0.5">{opt.hint}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* 3) Per-affirmation voice recordings (moved from main view) */}
      {theme && clipsApi && (
        <section data-testid="aff-settings-records" style={gpuStyle}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-pink-400" />
              <h3 className="text-pink-200 font-medium text-sm">Mes enregistrements</h3>
            </div>
            <span className="text-white/50 text-[10px]">
              {theme.affirmations.length} affirmations
            </span>
          </div>
          <p className="text-white/55 text-[11px] leading-relaxed mb-3">
            Enregistrez votre voix (ou celle d'un proche) pour chaque affirmation. Activez ensuite
            "Voix de mon choix" ci-dessus pour qu'elle soit jouée automatiquement.
          </p>

          {isAdmin && (
            <BulkUploadLudovic
              themeId={theme.id}
              maxIndex={theme.affirmations.length - 1}
              clipsApi={clipsApi}
            />
          )}

          {/* Affirmation selector */}
          <div className="rounded-xl border border-white/10 bg-slate-800 p-2.5 space-y-2 mb-3"
            style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate' }}>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRecordIndex((i) => Math.max(0, i - 1))}
                disabled={recordIndex === 0}
                className="px-2 py-1 rounded-lg bg-slate-700 text-white/70 disabled:opacity-30 hover:bg-slate-600"
                data-testid="aff-record-prev"
              >‹</button>
              <p className="flex-1 text-center text-white/80 text-[11px]">
                Affirmation <b>#{recordIndex + 1}</b> / {theme.affirmations.length}
              </p>
              <button
                onClick={() => setRecordIndex((i) => Math.min(theme.affirmations.length - 1, i + 1))}
                disabled={recordIndex === theme.affirmations.length - 1}
                className="px-2 py-1 rounded-lg bg-slate-700 text-white/70 disabled:opacity-30 hover:bg-slate-600"
                data-testid="aff-record-next"
              >›</button>
            </div>
            <p className="text-white text-sm italic leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
              "{theme.affirmations[recordIndex]}"
            </p>
          </div>

          {/* Recording slots */}
          <div className="grid grid-cols-1 gap-2">
            {[
              { id: 'ludovic', label: 'La voix de Ludovic', accent: 'amber', adminOnly: true },
              { id: 'mon-choix', label: 'La voix de mon choix', accent: 'emerald', adminOnly: false },
            ]
              .filter((s) => !s.adminOnly || isAdmin)
              .map((slot) => (
                <RecordSlotInline
                  key={slot.id}
                  slot={slot}
                  index={recordIndex}
                  clipsApi={clipsApi}
                  isAdmin={isAdmin}
                />
              ))}
          </div>
        </section>
      )}
    </>
  );
}

/**
 * BulkUploadLudovic — admin-only batch uploader for restoring all 34 Ludovic
 * clips of a theme at once. User selects multiple MP3 files; filenames must
 * start with the affirmation index (e.g. "00_je_suis...mp3", "1.mp3", "23-...").
 * Files are sequentially POSTed; a progress UI shows live status.
 */
function BulkUploadLudovic({ themeId, maxIndex, clipsApi }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });

  const parseIndex = (filename) => {
    // Accept: "00_x.mp3", "0.mp3", "1-x.mp3", "12.mp3", "23 my voice.mp3"
    const m = filename.match(/^(\d{1,3})[._\-\s]/);
    if (!m) {
      // also accept pure numeric filenames like "5.mp3"
      const m2 = filename.match(/^(\d{1,3})\.[a-z0-9]+$/i);
      if (!m2) return null;
      return parseInt(m2[1], 10);
    }
    return parseInt(m[1], 10);
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const valid = [];
    const skipped = [];
    Array.from(files).forEach((f) => {
      const idx = parseIndex(f.name);
      if (idx === null || isNaN(idx) || idx < 0 || idx > maxIndex) {
        skipped.push(f.name);
      } else {
        valid.push({ idx, file: f });
      }
    });
    if (valid.length === 0) {
      toast.error('Aucun nom de fichier reconnu. Format attendu : "0.mp3", "01_xxx.mp3", "23-affirmation.mp3"…');
      return;
    }
    if (skipped.length > 0) {
      toast.info(`${skipped.length} fichier(s) ignoré(s) (nom illisible)`);
    }
    valid.sort((a, b) => a.idx - b.idx);

    setBusy(true);
    setProgress({ done: 0, total: valid.length, current: '' });
    let ok = 0;
    let err = 0;
    for (let i = 0; i < valid.length; i++) {
      const { idx, file } = valid[i];
      setProgress({ done: i, total: valid.length, current: `#${idx} — ${file.name}` });
      try {
        await clipsApi.upload(idx, 'ludovic', file);
        ok++;
      } catch (e) {
        err++;
        console.error(`bulk upload failed idx=${idx}`, e);
      }
    }
    setProgress({ done: valid.length, total: valid.length, current: '' });
    setBusy(false);
    if (err === 0) toast.success(`${ok} clip(s) Ludovic restaurés ✓`);
    else toast.warning(`${ok} ok · ${err} échec(s)`);
  };

  return (
    <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 mb-3"
      style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate' }}
      data-testid="aff-bulk-upload">
      <div className="flex items-center gap-2 mb-1">
        <Upload className="w-4 h-4 text-amber-300" />
        <h4 className="text-amber-200 text-xs font-semibold">Upload en lot (admin)</h4>
      </div>
      <p className="text-white/65 text-[10px] leading-snug mb-2">
        Sélectionnez plusieurs MP3 d'un coup pour restaurer toutes les voix Ludovic.
        Nommez chaque fichier avec son numéro d'affirmation au début : <code className="text-amber-300">00_xxx.mp3</code>,
        <code className="text-amber-300"> 1.mp3</code>, <code className="text-amber-300">23-ma_voix.mp3</code>…
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        multiple
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        data-testid="aff-bulk-upload-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-full px-3 py-2 rounded-xl bg-amber-500 text-slate-900 text-xs font-semibold disabled:opacity-50 hover:bg-amber-400 transition flex items-center justify-center gap-2"
        data-testid="aff-bulk-upload-btn"
      >
        <Upload size={12} />
        {busy ? `Envoi… ${progress.done}/${progress.total}` : `Charger plusieurs MP3 (jusqu'à ${maxIndex + 1})`}
      </button>
      {busy && (
        <div className="mt-2 space-y-1">
          <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 transition-all"
              style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
          {progress.current && (
            <p className="text-amber-200 text-[9px] truncate" title={progress.current}>
              {progress.current}
            </p>
          )}
        </div>
      )}
    </div>
  );
}


function RecordSlotInline({ slot, index, clipsApi, isAdmin }) {
  const fileRef = useRef(null);
  const clip = clipsApi.getClip(index, slot.id);

  return (
    <div className="rounded-xl border border-white/10 bg-slate-800 p-3 space-y-2"
      style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate' }}
      data-testid={`aff-rec-slot-${slot.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-white/90 text-xs font-medium">{slot.label}</p>
        {clip && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300">Enregistrée</span>}
      </div>
      {clip && (
        <audio controls preload="metadata" src={`${API}${clip.url}`}
          className="w-full block"
          style={{ height: '40px', backgroundColor: '#1e293b', borderRadius: '6px' }}
          data-testid={`aff-rec-audio-${slot.id}`} />
      )}
      <input ref={fileRef} type="file" accept="audio/*" className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0]; e.target.value = '';
          if (!f) return;
          if (f.size > 20 * 1024 * 1024) { toast.error('Audio trop volumineux (20 Mo max)'); return; }
          try { await clipsApi.upload(index, slot.id, f); toast.success('Voix enregistrée'); }
          catch (err) { toast.error(err?.response?.data?.detail || 'Échec'); }
        }}
        data-testid={`aff-rec-file-${slot.id}`} />
      <div className="flex flex-wrap gap-1.5">
        <LiveRecorder
          label={clip ? 'Réenregistrer micro' : 'Enregistrer micro'}
          accent={slot.accent}
          testIdPrefix={`aff-rec-${slot.id}`}
          disabled={clip?.locked}
          onRecorded={async (blob) => {
            try {
              await clipsApi.uploadBlob(index, slot.id, blob,
                slot.id === 'ludovic' ? `ludovic_${index}.mp3` : `monchoix_${index}.mp3`);
              toast.success('Voix enregistrée en MP3');
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Échec');
            }
          }}
        />
        <button onClick={() => fileRef.current?.click()} disabled={clip?.locked}
          className="flex-1 text-[10px] flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-slate-800/70 text-white/70 border border-white/10 hover:bg-slate-700 disabled:opacity-40"
          data-testid={`aff-rec-upload-${slot.id}`}>
          <Upload size={10} /> Fichier
        </button>
        {clip && (
          <>
            {(isAdmin || slot.id === 'mon-choix') && (
              <button onClick={async () => {
                try { await clipsApi.toggleLock(index, slot.id); }
                catch (err) { toast.error(err?.response?.data?.detail || 'Action impossible'); }
              }}
                className={`text-[10px] px-2 py-1.5 rounded-lg border ${clip.locked ? 'bg-amber-500 text-slate-900 border-amber-400' : 'bg-slate-800 text-white/70 border-white/10'}`}
                data-testid={`aff-rec-lock-${slot.id}`}>
                {clip.locked ? <Lock size={10} /> : <Unlock size={10} />}
              </button>
            )}
            {!clip.locked && (
              <button onClick={async () => {
                if (!window.confirm('Supprimer ce clip ?')) return;
                try { await clipsApi.remove(index, slot.id); toast.success('Supprimé'); }
                catch (err) { toast.error(err?.response?.data?.detail || 'Échec'); }
              }}
                className="text-[10px] px-2 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30"
                data-testid={`aff-rec-delete-${slot.id}`}>
                <Trash2 size={10} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const VOICE_SLOTS = [
  { id: 'homme', label: 'Voix Homme' },
  { id: 'femme', label: 'Voix Femme' },
  { id: 'seance', label: 'Séance complète' },
];

function VoiceSlotRow({ section, slot, voice, isAdmin, onChange, audioState, onToggle, onVolume }) {
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error('Audio trop volumineux (30 Mo max)'); return; }
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/api/session-audio/${section}/voice/${slot.id}/upload`, fd);
      toast.success('Voix téléversée'); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer la ${slot.label} ?`)) return;
    try {
      await axios.delete(`${API}/api/session-audio/${section}/voice/${slot.id}`);
      toast.success('Voix supprimée'); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
  };
  const lock = async () => {
    try {
      await axios.put(`${API}/api/session-audio/${section}/voice/${slot.id}/lock`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3.5 space-y-3" data-testid={`voice-slot-${section}-${slot.id}`}>
      <input ref={fileRef} type="file" accept="audio/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value=''; }}
        data-testid={`voice-input-${section}-${slot.id}`} />
      <div className="flex items-center gap-2">
        <Mic className="w-4 h-4 text-amber-400 flex-shrink-0" />
        <p className="text-white text-sm font-medium flex-1">{slot.label}</p>
        {voice && (
          <button onClick={() => onToggle(`voice-${slot.id}`, voice.url)}
            className={`p-1.5 rounded-full transition ${audioState?.playing ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-amber-300 hover:bg-slate-700'}`}
            data-testid={`voice-play-${section}-${slot.id}`}>
            {audioState?.playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {voice ? (
        <>
          <p className="text-white/40 text-[11px] truncate">{voice.filename}</p>
          {audioState?.playing && (
            <div className="flex items-center gap-2">
              <Volume2 className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
              <input type="range" min="0" max="1" step="0.05" value={audioState.volume}
                onChange={(e) => onVolume(`voice-${slot.id}`, parseFloat(e.target.value))}
                className="flex-1 accent-amber-500" />
            </div>
          )}
          {isAdmin && (
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => fileRef.current?.click()} disabled={voice.locked}
                className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`voice-replace-${section}-${slot.id}`}>
                <RefreshCw className="w-3 h-3" /> Remplacer
              </button>
              <button onClick={lock}
                className={`text-[10px] flex items-center gap-1 px-2 py-1 rounded-full border transition ${voice.locked ? 'bg-amber-500 text-slate-900 border-amber-400' : 'bg-slate-800 text-white/70 border-white/10 hover:bg-slate-700'}`}
                data-testid={`voice-lock-${section}-${slot.id}`}>
                {voice.locked ? <><Lock className="w-3 h-3" /> Verrouillée</> : <><Unlock className="w-3 h-3" /> Verrouiller</>}
              </button>
              {!voice.locked && (
                <button onClick={remove}
                  className="text-[10px] flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25"
                  data-testid={`voice-delete-${section}-${slot.id}`}>
                  <Trash2 className="w-3 h-3" /> Supprimer
                </button>
              )}
            </div>
          )}
        </>
      ) : isAdmin ? (
        <button onClick={() => fileRef.current?.click()}
          className="w-full text-xs flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-amber-500/15 text-amber-300 border border-amber-400/30 hover:bg-amber-500/25"
          data-testid={`voice-upload-${section}-${slot.id}`}>
          <Upload className="w-3.5 h-3.5" /> Charger un audio
        </button>
      ) : (
        <p className="text-white/30 text-xs italic">Non disponible.</p>
      )}
    </div>
  );
}

function MusicRow({ section, music, audioState, onToggle, onVolume, onRemove }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 space-y-2" data-testid={`music-row-${music.id}`}>
      <div className="flex items-center gap-2">
        <MusicIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
        <p className="text-white text-sm flex-1 truncate" title={music.name}>{music.name}</p>
        <button onClick={() => onToggle(`music-${music.id}`, music.url)}
          className={`p-1.5 rounded-full transition ${audioState?.playing ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-emerald-300 hover:bg-slate-700'}`}
          data-testid={`music-play-${music.id}`}>
          {audioState?.playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => onRemove(music.id)}
          className="p-1.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25"
          title="Supprimer" data-testid={`music-delete-${music.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {audioState?.playing && (
        <div className="flex items-center gap-2">
          <Volume2 className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
          <input type="range" min="0" max="1" step="0.05" value={audioState.volume}
            onChange={(e) => onVolume(`music-${music.id}`, parseFloat(e.target.value))}
            className="flex-1 accent-emerald-500" />
        </div>
      )}
    </div>
  );
}

export default function SessionAudioPanel({
  isOpen, onClose, section, isAdmin = false, showVoices = true,
  affirmationsSettings = null,
  onAffirmationsSettingsChange = null,
  affirmationsTheme = null,
  affirmationsClipsApi = null,
}) {
  const [voices, setVoices] = useState([]);
  const [musics, setMusics] = useState([]);
  const [ambientSounds, setAmbientSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioStates, setAudioStates] = useState({}); // key -> { playing, volume }
  const audiosRef = useRef({}); // key -> HTMLAudioElement
  const musicFileRef = useRef(null);
  const [musicNameDraft, setMusicNameDraft] = useState('');
  const [pendingFile, setPendingFile] = useState(null);

  const {
    activeAmbientSounds = {}, ambientVolumes = {}, toggleAmbientSound, updateAmbientVolume,
  } = useZen();

  const refresh = useCallback(async () => {
    try {
      const [r, amb] = await Promise.all([
        axios.get(`${API}/api/session-audio/${section}`),
        axios.get(`${API}/api/ambient-sounds`),
      ]);
      setVoices(r.data?.voices || []);
      setMusics(r.data?.musics || []);
      setAmbientSounds(amb.data || []);
    } finally { setLoading(false); }
  }, [section]);

  useEffect(() => { if (isOpen) refresh(); }, [isOpen, refresh]);

  // Stop all <audio> when closing
  useEffect(() => {
    if (!isOpen) {
      Object.values(audiosRef.current).forEach((a) => { try { a.pause(); a.currentTime = 0; } catch {} });
      audiosRef.current = {};
      setAudioStates({});
    }
  }, [isOpen]);

  const toggleAudio = (key, url) => {
    let a = audiosRef.current[key];
    if (!a) {
      a = new Audio(`${API}${url}`);
      a.loop = true; a.volume = 0.7;
      audiosRef.current[key] = a;
    }
    setAudioStates((prev) => {
      const cur = prev[key]?.playing;
      if (cur) { a.pause(); return { ...prev, [key]: { playing: false, volume: a.volume } }; }
      a.play().catch(() => toast.error('Lecture impossible'));
      return { ...prev, [key]: { playing: true, volume: a.volume } };
    });
  };

  const setAudioVol = (key, v) => {
    const a = audiosRef.current[key];
    if (a) a.volume = v;
    setAudioStates((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), volume: v, playing: prev[key]?.playing } }));
  };

  const voiceFor = (slotId) => voices.find((v) => v.slot === slotId);

  const onMusicFile = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error('Audio trop volumineux (30 Mo max)'); return; }
    setPendingFile(file);
    setMusicNameDraft(file.name.replace(/\.[^.]+$/, '').slice(0, 80));
  };

  const submitMusic = async () => {
    if (!pendingFile) return;
    const fd = new FormData();
    fd.append('file', pendingFile);
    fd.append('name', musicNameDraft.trim() || pendingFile.name);
    try {
      await axios.post(`${API}/api/session-audio/${section}/music/upload`, fd);
      toast.success('Musique ajoutée');
      setPendingFile(null); setMusicNameDraft('');
      if (musicFileRef.current) musicFileRef.current.value = '';
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
  };

  const removeMusic = async (id) => {
    if (!window.confirm('Supprimer cette musique ?')) return;
    try {
      // also stop if playing
      const key = `music-${id}`;
      const a = audiosRef.current[key];
      if (a) { try { a.pause(); } catch {} delete audiosRef.current[key]; }
      setAudioStates((p) => { const np = { ...p }; delete np[key]; return np; });
      await axios.delete(`${API}/api/session-audio/${section}/music/${id}`);
      toast.success('Musique supprimée');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
  };

  if (!isOpen) return null;

  // Android Chrome GPU compositing fix: force isolation + translateZ on the
  // modal stack to prevent the "noise/grain" rendering glitch that happens
  // when this overlay stacks atop another fixed/backdrop-filtered overlay.
  const overlayGpu = { transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate', contain: 'paint' };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center mobile-safe-overlay" data-testid={`session-audio-panel-${section}`} style={overlayGpu}>
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto bg-slate-950 sm:rounded-3xl rounded-t-3xl border border-amber-500/20 shadow-2xl mobile-safe-surface" style={overlayGpu}>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 pt-10 pb-4 bg-slate-950 border-b border-amber-500/10" style={overlayGpu}>
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-400" />
            <h2 className="text-amber-300 font-semibold">Paramétrer votre séance</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-white/60" data-testid="session-audio-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 pb-24 space-y-6">
          {/* Affirmations-specific: voice mode + interval + per-affirmation recordings */}
          {section === 'affirmations' && affirmationsSettings && (
            <AffirmationsSettingsBlock
              settings={affirmationsSettings}
              onChange={onAffirmationsSettingsChange}
              theme={affirmationsTheme}
              clipsApi={affirmationsClipsApi}
              isAdmin={isAdmin}
            />
          )}

          {/* Voices block */}
          {showVoices && section !== 'affirmations' && (
          <section data-testid="session-voices">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-4 h-4 text-amber-400" />
              <h3 className="text-amber-200 font-medium text-sm">Voix de séance {isAdmin ? '(admin)' : ''}</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {VOICE_SLOTS.map((slot) => (
                <VoiceSlotRow
                  key={slot.id} section={section} slot={slot} voice={voiceFor(slot.id)}
                  isAdmin={isAdmin} onChange={refresh}
                  audioState={audioStates[`voice-${slot.id}`]}
                  onToggle={toggleAudio} onVolume={setAudioVol}
                />
              ))}
            </div>
            {!isAdmin && voices.length === 0 && (
              <p className="text-white/40 text-xs italic mt-2">Aucune voix disponible pour le moment.</p>
            )}
          </section>
          )}

          {/* Ambient sounds */}
          <section data-testid="session-ambient">
            <div className="flex items-center gap-2 mb-3">
              <Headphones className="w-4 h-4 text-cyan-400" />
              <h3 className="text-cyan-200 font-medium text-sm">Sons ambiants</h3>
            </div>
            {loading ? (
              <p className="text-white/40 text-xs">Chargement…</p>
            ) : ambientSounds.length === 0 ? (
              <p className="text-white/40 text-xs italic">Aucun son ambiant disponible.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {ambientSounds.map((s) => {
                  const active = !!activeAmbientSounds[s.id];
                  return (
                    <div key={s.id} className={`rounded-xl border p-2.5 transition ${active ? 'bg-cyan-500/10 border-cyan-400/40' : 'bg-white/[0.03] border-white/10'}`}
                      data-testid={`ambient-row-${s.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <button onClick={() => toggleAmbientSound?.(s)}
                          className={`p-1.5 rounded-full transition ${active ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-cyan-300 hover:bg-slate-700'}`}
                          data-testid={`ambient-toggle-${s.id}`}>
                          {active ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                        </button>
                        <span className="text-white text-xs truncate flex-1" title={s.name}>{s.name}</span>
                      </div>
                      {active && (
                        <input type="range" min="0" max="1" step="0.05"
                          value={ambientVolumes[s.id] ?? 0.5}
                          onChange={(e) => updateAmbientVolume?.(s.id, parseFloat(e.target.value))}
                          className="w-full accent-cyan-500" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Favorite musics */}
          <section data-testid="session-musics">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MusicIcon className="w-4 h-4 text-emerald-400" />
                <h3 className="text-emerald-200 font-medium text-sm">
                  Mes musiques préférées <span className="text-white/40 font-normal">({musics.length}/5)</span>
                </h3>
              </div>
              {musics.length < 5 && (
                <button onClick={() => musicFileRef.current?.click()}
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/25"
                  data-testid="music-add-btn">
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </button>
              )}
              <input ref={musicFileRef} type="file" accept="audio/*" className="hidden"
                onChange={(e) => { onMusicFile(e.target.files?.[0]); }}
                data-testid="music-file-input" />
            </div>

            {pendingFile && (
              <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-3 mb-3 space-y-2" data-testid="music-pending">
                <p className="text-white/70 text-xs">Fichier sélectionné : <span className="text-emerald-300">{pendingFile.name}</span></p>
                <input type="text" value={musicNameDraft} maxLength={80}
                  onChange={(e) => setMusicNameDraft(e.target.value)}
                  placeholder="Nom de la musique"
                  className="w-full bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-400/50 focus:outline-none"
                  data-testid="music-name-input" />
                <div className="flex gap-2">
                  <button onClick={submitMusic}
                    className="flex-1 text-xs px-3 py-2 rounded-lg bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400"
                    data-testid="music-submit">
                    Enregistrer
                  </button>
                  <button onClick={() => { setPendingFile(null); setMusicNameDraft(''); if (musicFileRef.current) musicFileRef.current.value=''; }}
                    className="text-xs px-3 py-2 rounded-lg bg-slate-800 text-white/70 hover:bg-slate-700"
                    data-testid="music-cancel">
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <p className="text-white/40 text-xs">Chargement…</p>
            ) : musics.length === 0 && !pendingFile ? (
              <p className="text-white/40 text-xs italic">Ajoutez jusqu'à 5 musiques relaxantes.</p>
            ) : (
              <div className="space-y-2">
                {musics.map((m) => (
                  <MusicRow key={m.id} section={section} music={m}
                    audioState={audioStates[`music-${m.id}`]}
                    onToggle={toggleAudio} onVolume={setAudioVol} onRemove={removeMusic} />
                ))}
              </div>
            )}
          </section>

          <p className="text-white/30 text-[11px] flex items-center gap-1.5 italic">
            <Sparkles className="w-3 h-3 flex-shrink-0" />
            Les voix, sons et musiques peuvent jouer ensemble — ajustez les volumes pour votre mix idéal.
          </p>
        </div>
      </div>
    </div>
  );
}
